import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, Location as AngularLocation } from '@angular/common';
import { ItemSelectorComponent } from 'src/app/shared/components/item-selector/item-selector.component';
import { Item, ShopCategory } from 'src/app/shared/models/items.model';
import { MatCardModule } from '@angular/material/card';
import { MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { ItemPriceCardComponent } from 'src/app/shared/components/item-price-card/item-price-card.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { LocationsLegendComponent } from 'src/app/shared/components/locations-legend/locations-legend.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { BasePage } from 'src/app/shared/base-classes/base-page';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FAME_BONUS_PREMIUM, PRICE_DISPLAY_OPTIONS, getAllPriceDisplayOptions } from 'src/app/shared/constants/market.constant';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PriceDisplayOption } from 'src/app/shared/models/price.model';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-price-check',
  imports: [
    CommonModule,
    FormsModule,
    ItemSelectorComponent,
    MatDivider,
    MatCardModule,
    MatInputModule,
    ItemPriceCardComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    LocationsLegendComponent,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatExpansionModule,
    MatButtonToggleModule,
  ],
  templateUrl: './price-check.component.html',
  styleUrls: ['./price-check.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceCheckComponent extends BasePage {
  disableRefresh: boolean = false;
  disableSaveConfig: boolean = false;
  priceDisplayOptions = getAllPriceDisplayOptions();
  fameBonusPremium = FAME_BONUS_PREMIUM;
  Math = Math;

  constructor(
    route: ActivatedRoute,
    router: Router,
    urlLocation: AngularLocation,
    jsonReaderService: JsonReaderService,
    aoDataGetter: AoDataGetterService,
    albionImageService: AlbionImageService,
    userSettingsService: UserSettingsService,
    cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: Object,
    private snackBar: MatSnackBar
  ) {
    super(route, router, urlLocation, jsonReaderService, aoDataGetter, albionImageService, userSettingsService, cdr, platformId);
  }

  filterItemsForSelect(items: Item[]): Item[] {
    return items;
  }

  filterShopCategoriesForSelect(categories: ShopCategory[]): ShopCategory[] {
    return categories;
  }

  filterItemsForPrices(items: Item[]): Item[] {
    if (items && this.selectedItem) {
      return items.filter((item) => item.groupUniqueName === this.selectedItem?.groupUniqueName);
    } else {
      return [];
    }
  }

  filterItemsToShow(items: Item[]): Item[] {
    if (items && this.selectedItem) {
      const option: PriceDisplayOption = this.userSettingsService.getUserPriceConfig().priceDisplayOption;

      switch (option) {
        case PRICE_DISPLAY_OPTIONS.single:
          return [this.selectedItem];
        case PRICE_DISPLAY_OPTIONS.tier:
          return items.filter((item) => item.groupUniqueName === this.selectedItem?.groupUniqueName && item.tier === this.selectedItem.tier);
        case PRICE_DISPLAY_OPTIONS.all:
          return items.filter((item) => item.groupUniqueName === this.selectedItem?.groupUniqueName);
        default:
          return [];
      }
    } else {
      return [];
    }
  }

  filterPricesForCard(item: Item, prices: MarketResponse[] | null): MarketResponse[] | null {
    return prices ? prices.filter((x) => x.itemTypeId === item.uniqueName && this.selectedCities.includes(x.city)) : null;
  }

  trackById(index: number, item: Item): string {
    return item.uniqueName;
  }

  refreshPrices(): void {
    this.disableRefresh = true;
    this.updatePrices(this.items ?? []);
    this.updateHistories(this.items ?? []);
    setTimeout(() => {
      this.disableRefresh = false;
      this.cdr.markForCheck();
    }, 5000);
  }

  async savePriceConfiguration(): Promise<void> {
    if (this.userSettingsService.isLoggedIn) {
      this.disableSaveConfig = true;

      await this.userSettingsService.updateUserPriceConfig(this.userSettingsService.getUserPriceConfig());

      setTimeout(() => {
        this.disableSaveConfig = false;
        this.cdr.markForCheck();
      }, 5000);
    } else {
      this.snackBar.open(`You must be Logged In to save!`, undefined, { duration: 2000 });
    }
  }
}
